import React, { useState, useEffect } from "react";
import DraftEmail from "../emailTriaging/draftEmail";
import "../emailTriaging/draftEmail.css"
// email_center.css should be imported inside email_center.js already

function makeId() {
  const bytes = crypto.getRandomValues(new Uint8Array(5));
  return Array.from(bytes, b => b.toString(36)).join("");
}

const SAMPLE_PO = {
  poNumber: "PO-12345",
  materialCode: "MAT-9876",
};

const initialEmails = [
  {
    id: "e1",
    subject: "Delivery delay",
    to: "vendor@example.com",
    body: "We expect a 2-day delay.",
    isDraft: false,
    alertId: "A100",
    materialId: "MAT-9876",
    timestamp: new Date().toLocaleString(),
  },
  {
    id: "e2",
    subject: "Invoice request",
    to: "accounts@example.com",
    body: "Please send invoice for PO-12345.",
    isDraft: true,
    alertId: "A102",
    materialId: "MAT-9876",
    timestamp: new Date().toLocaleString(),
  },
];

export default function Testing() {
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedPO] = useState(SAMPLE_PO);
  const [emails, setEmails] = useState(() => {
    try {
      const stored = localStorage.getItem("emails_demo");
      return stored ? JSON.parse(stored) : initialEmails;
    } catch {
      return initialEmails;
    }
  });

  // Editor state
  const [searchQuery, setSearchQuery] = useState("");
  const [emailListTab, setEmailListTab] = useState("sent"); // "sent" | "drafts"
  const [selectedEmailId, setSelectedEmailId] = useState(null); // opens a preview in right side when not null
  const [activeTab, setActiveTab] = useState("sent"); // used when viewing a selected email
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [draftSaved, setDraftSaved] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Persist emails
  useEffect(() => {
    localStorage.setItem("emails_demo", JSON.stringify(emails));
  }, [emails]);

  // derived lists
  const sentEmailsOnly = emails.filter((e) => !e.isDraft);

  // displayedEmails for left panel (depends on tab & search)
  const displayedEmails = emails.filter((e) => {
    const tabMatches = emailListTab === "sent" ? !e.isDraft : e.isDraft;
    if (!tabMatches) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (e.subject || "").toLowerCase().includes(q) ||
      (e.to || "").toLowerCase().includes(q) ||
      (e.body || "").toLowerCase().includes(q)
    );
  });

  // selectedEmail object used in right panel conditional
  const selectedEmail = emails.find((e) => e.id === selectedEmailId) || null;

  // handlers
  function handleOpenEmailModal() {
    setEmailModalOpen(true);
  }

  function handleCloseEmailModal() {
    setEmailModalOpen(false);
    // reset composer state (optional)
    setEmailTo("");
    setEmailSubject("");
    setEmailBody("");
    setDraftSaved(false);
    setEmailSent(false);
    setSelectedEmailId(null);
  }

  function handleSaveDraft() {
    setDraftSaved(true);
    const now = new Date().toLocaleString();
    const randomNumber = crypto.getRandomValues(new Uint32Array(1))[0] % 1000;
    // If composer fields are empty, create a minimal draft
    const newDraft = {
      id: makeId(),
      subject: emailSubject || "(no subject)",
      to: emailTo || "",
      body: emailBody || "",
      isDraft: true,
      alertId: `A${randomNumber}`,
      materialId: selectedPO.materialCode,
      timestamp: now,
    };

    setEmails((prev) => [newDraft, ...prev]);
    setTimeout(() => setDraftSaved(false), 1200);
  }

  function handleSendEmail() {
    setEmailSent(true);
    const now = new Date().toLocaleString();
    const randomNumber = crypto.getRandomValues(new Uint32Array(1))[0] % 1000;
    // create a sent email record
    const sent = {
      id: makeId(),
      subject: emailSubject || "(no subject)",
      to: emailTo || "",
      body: emailBody || "",
      isDraft: false,
      alertId: `A${randomNumber}`,
      materialId: selectedPO.materialCode,
      timestamp: now,
    };
    setEmails((prev) => [sent, ...prev]);
    // optionally remove any matching drafts (simple approach)
    setEmails((prev) => prev.filter((e) => !(e.isDraft && e.subject === emailSubject && e.to === emailTo)));
    setTimeout(() => setEmailSent(false), 1200);

    // reset composer
    setEmailTo("");
    setEmailSubject("");
    setEmailBody("");
  }

  function handleDeleteEmail(id, e) {
    if (e) e.stopPropagation();
    if (!window.confirm("Delete this email?")) return;
    setEmails((prev) => prev.filter((x) => x.id !== id));
    if (selectedEmailId === id) setSelectedEmailId(null);
  }

  function deleteDraft(poNumber) {
    // remove all drafts for this PO (demo)
    if (!window.confirm(`Delete all drafts for ${poNumber}?`)) return;
    setEmails((prev) => prev.filter((x) => !x.isDraft || x.materialId !== selectedPO.materialCode));
  }

  return (
    <div>
      <button onClick={handleOpenEmailModal}>Open Email Center</button>

      <DraftEmail
        emailModalOpen={emailModalOpen}
        selectedPO={selectedPO}
        handleCloseEmailModal={handleCloseEmailModal}
        sentEmailsOnly={sentEmailsOnly}
        poEmails={emails.filter((e) => e.materialId === selectedPO.materialCode)}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        emailListTab={emailListTab}
        setEmailListTab={setEmailListTab}
        displayedEmails={displayedEmails}
        selectedEmailId={selectedEmailId}
        setSelectedEmailId={setSelectedEmailId}
        handleDeleteEmail={handleDeleteEmail}
        draftSaved={draftSaved}
        handleSaveDraft={handleSaveDraft}
        emailSent={emailSent}
        handleSendEmail={handleSendEmail}
        emailTo={emailTo}
        setEmailTo={setEmailTo}
        emailSubject={emailSubject}
        setEmailSubject={setEmailSubject}
        emailBody={emailBody}
        setEmailBody={setEmailBody}
        selectedEmail={selectedEmail}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        deleteDraft={deleteDraft}
      />
    </div>
  );
}